import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { LoggerModule } from 'ngx-logger';
import { environment } from '../../environments/environment';
import { provideRouter, Router, Routes, withInMemoryScrolling } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideEnvironment } from './providers/environment.provider';
import { MixpanelService } from '@movinmotion/data-access-third-party';
import { environment as sharedEnvironment, getAppConfigProvider } from '@movinmotion/conf-shared';
// eslint-disable-next-line @nx/enforce-module-boundaries
import pkg from '../../../../../package.json';
import { provideFirebaseAndAuth, WithCredentialsInterceptor } from '@movinmotion/data-access-auth';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { LegacyDateNormalizationInterceptor, provideLocale } from '@movinmotion/util-ngx';
import { provideCustomDateFnsAdapter, provideDsConfig } from '@movinmotion/design-system';
import { provideTranslocoConfig } from '@movinmotion/util-translate';
import { provideShareMaterial } from '@movinmotion/ui-shared-material-legacy';
import { crossOriginXsrfInterceptor, provideCrossOriginXsrfConfig } from '@movinmotion/shared/data-access/api';

export interface CoreOptions {
  routes: Routes;
}

export function provideCore({ routes }: CoreOptions) {
  return [
    importProvidersFrom([LoggerModule.forRoot(environment.logger)]),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([crossOriginXsrfInterceptor])),
    provideCrossOriginXsrfConfig({
      trustedApiOrigins: [sharedEnvironment.cookieDomain],
    }),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideTranslocoConfig(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LegacyDateNormalizationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WithCredentialsInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideCustomDateFnsAdapter(),
    provideLocale({
      locales: ['fr', 'en'],
      queryParam: environment?.queryParamsKeys?.language,
    }),
    provideEnvironment(),
    getAppConfigProvider({
      appId: 'AUTH',
      version: pkg.version,
    }),
    provideShareMaterial(),
    MixpanelService,
    provideFirebaseAndAuth(environment.firebase),
    provideDsConfig(),
  ];
}
